import { useContext, useState } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import Tag, { TAG_COLORS, TAG_SIZES } from "components/DesignComponents/Tag";
import { useGetSingleTaskGroupEndpoint } from "components/TaskGroups/hooks";
import AlertContext from "context/alert/alertContext";
import ParamsContext from "context/params/paramsContext";
import TableContext from "context/table/tableContext";
import {
    ENDPOINTS,
    maxDialogWidth,
    TABLE_NAMES,
    TRI_SOURCES,
    useApi,
    useFormattedNumber,
    useMediaQueries
} from "shared";

import { TASK_ACTIONS } from "../../../types";
import DialogActions from "../../components/DialogActions";
import DialogWithConfirmation from "../../components/DialogWithConfirmation";
import { CancelSelectedTasksProps, MainItemDetails } from "./types";

const CancelSelectedTasks = ({
    mainTableName,
    isOpen,
    close
}: CancelSelectedTasksProps) => {
    const { setBackgroundActionId } = useContext(AlertContext);
    const { setReloadItems } = useContext(ParamsContext);

    const { outerDetailsData, selectedRows, deselectAllRows } =
        useContext(TableContext);

    const { bulkCancel, handleResponse, updateData } = useApi();
    const { getSingleTaskGroupData } = useGetSingleTaskGroupEndpoint();
    const { renderFormattedNumber } = useFormattedNumber();
    const { fromMd, toMd } = useMediaQueries();
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);

    const resourceCount = selectedRows?.length;
    const isSingle = resourceCount === 1;

    const handleCloseCancelTasksDialog = () => close(TASK_ACTIONS.CancelTasks);

    const deselectAndClose = () => {
        deselectAllRows();
        handleCloseCancelTasksDialog();
    };

    const handleManualUpdate = () => {
        if (mainTableName === TABLE_NAMES.TaskGroups) {
            const outerData: MainItemDetails | null = outerDetailsData;
            const id = outerData?.detailsData.id;
            const updateMainRowData = outerData?.updateMainRowData;

            getSingleTaskGroupData(id, updateMainRowData);
        }

        if (mainTableName === TABLE_NAMES.DeviceGroups) {
            setReloadItems(TABLE_NAMES.Tasks);
        }
    };

    const submitCancelTask = async () => {
        try {
            setIsLoading(true);

            const singlePayload = { status_id: 4 };

            const bulkPayload = {
                source: TRI_SOURCES.Selected,
                [TRI_SOURCES.Selected]: selectedRows
            };

            const response = await (isSingle
                ? updateData(ENDPOINTS.Tasks, selectedRows[0], singlePayload)
                : bulkCancel(ENDPOINTS.Tasks, bulkPayload));

            if (isSingle) {
                deselectAndClose();
                handleManualUpdate();
                handleResponse(response);
            } else {
                setBackgroundActionId(
                    response.data,
                    TABLE_NAMES.Tasks,
                    () => {
                        deselectAndClose();
                        handleManualUpdate();
                    },
                    () => setIsLoading(false)
                );
            }
        } catch (error) {
            handleResponse(error);
            setIsLoading(false);
        }
    };

    const description = t(
        "Dialog##confirmations##cancel task confirmation##first part"
    );

    const confirmationDescription = `
        ${description} 
        ${t("Dialog##confirmations##cancel task confirmation##second part")}
    `;

    const isBulkActionLoading = !isSingle && isLoading;

    return (
        <DialogWithConfirmation
            data-testid="cancel-tasks-for-selection-dialog"
            title={t("Dialog##cancel tasks")}
            description={t(
                "Dialog##confirmations##cancel task confirmation##first part"
            )}
            isActionsSeparator
            isTitleSeparator
            open={isOpen}
            count={resourceCount}
            source={TRI_SOURCES.Selected}
            confirmationDescription={confirmationDescription}
            submit={submitCancelTask}
            close={handleCloseCancelTasksDialog}
            isBulkActionLoading={isBulkActionLoading}
            css={css(fromMd && maxDialogWidth)}
            extendToMaxWidth={toMd}
            actions={
                <>
                    <DialogActions
                        onClose={handleCloseCancelTasksDialog}
                        isLoading={isBulkActionLoading || isLoading}
                        loaderText={
                            !isSingle ? t("Button##cancelling") : undefined
                        }
                        isReadyToConfirm
                        confirmationButtonText={t("Button##confirm")}
                        cancelButtonTestId="cancel-cancel-tasks"
                        confirmationButtonTestId="confirm-cancel-tasks"
                    />
                </>
            }
            slotProps={{
                transition: {
                    onExited: () => setIsLoading(false)
                }
            }}
        >
            <Tag
                data-testid="cancel-tasks-number"
                size={TAG_SIZES.Medium}
                color={TAG_COLORS.Primary}
                title={`${t("Dialog##selected tasks")}: ${renderFormattedNumber(
                    resourceCount
                )}`}
            />
        </DialogWithConfirmation>
    );
};
export default CancelSelectedTasks;
